import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  RefreshCw, Plus, CheckCircle, XCircle, Download, Upload, DollarSign,
  Trash2, Music, Loader2, BarChart3, Package, Settings, Send, ChevronDown, AlertTriangle, X, ChevronRight,
  Play, Pause
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const GENRES = [
  'Afro House', 'Amapiano', 'Ambient', 'Bachata', 'Bass House', 'Bolero', 'Bossa Nova',
  'Cumbia', 'Dance Pop', 'Deep House', 'Dembow', 'Disco', 'Drill', 'Drum & Bass',
  'Dubstep', 'EDM', 'Electro House', 'Electronic', 'Flamenco', 'Funk', 'Future Bass',
  'Garage', 'Grime', 'Hip-Hop', 'House', 'Indie', 'Jazz', 'Jersey Club', 'Jungle',
  'K-Pop', 'Latin Pop', 'Lo-Fi', 'Merengue', 'Minimal', 'Moombahton', 'Neo Soul',
  'Pop', 'Progressive House', 'R&B', 'Reggae', 'Reggaeton', 'Rock', 'Salsa',
  'Soca', 'Soul', 'Synthwave', 'Tech House', 'Techno', 'Trance', 'Trap',
  'Tropical House', 'UK Garage', 'Vallenato', 'Vaporwave',
];

const STATUS_LABELS = {
  generado: { label: 'Generado', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
  pendiente_mastering: { label: 'En Mastering', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
  masterizado: { label: 'Masterizado', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  pendiente_aprobacion: { label: 'Pendiente Aprobacion', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  publicado: { label: 'Publicado', color: 'bg-green-500/15 text-green-400 border-green-500/30' },
  archivo_empresa: { label: 'Archivo Empresa', color: 'bg-purple-500/15 text-purple-400 border-purple-500/30' },
};

const BATCH_PHASE_STYLES = {
  in_progress: { border: 'border-red-500/30', bg: 'bg-red-500/5', icon_bg: 'bg-red-500/15', icon_color: 'text-red-400', badge_bg: 'bg-red-500/15', badge_color: 'text-red-400', label: 'EN PROCESO' },
  pending_approval: { border: 'border-blue-500/30', bg: 'bg-blue-500/5', icon_bg: 'bg-blue-500/15', icon_color: 'text-blue-400', badge_bg: 'bg-blue-500/15', badge_color: 'text-blue-400', label: 'PENDIENTE APROBACION' },
  completed: { border: 'border-green-500/30', bg: 'bg-green-500/5', icon_bg: 'bg-green-500/15', icon_color: 'text-green-400', badge_bg: 'bg-green-500/15', badge_color: 'text-green-400', label: 'COMPLETADO' },
};

const ProductionPanel = ({ isAdmin = false }) => {
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [tracksTotal, setTracksTotal] = useState(0);
  const [batches, setBatches] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterGenre, setFilterGenre] = useState('');
  // Request form — single genre
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [reqGenre, setReqGenre] = useState('');
  const [reqQuantity, setReqQuantity] = useState(20);
  const [reqBpmMin, setReqBpmMin] = useState(80);
  const [reqBpmMax, setReqBpmMax] = useState(160);
  const [reqVibe, setReqVibe] = useState('');
  const [reqNotes, setReqNotes] = useState('');
  const [reqLanguage, setReqLanguage] = useState('Espanol');
  const [reqTrackNames, setReqTrackNames] = useState('');
  // Mastering batch state
  const [batchSummary, setBatchSummary] = useState(null);
  const [pendingByGenre, setPendingByGenre] = useState([]);
  const [batchUploadFiles, setBatchUploadFiles] = useState({});
  const [batchUploading, setBatchUploading] = useState(null);
  const [batchUploadProgress, setBatchUploadProgress] = useState({ done: 0, total: 0 });
  const [batchPrices, setBatchPrices] = useState({});
  const [batchPlaylist, setBatchPlaylist] = useState({});
  const [expandedBatch, setExpandedBatch] = useState(null);
  // Generation polling
  const [generatingReqId, setGeneratingReqId] = useState(null);
  const [genProgress, setGenProgress] = useState(null);
  // Upload general
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  // Price
  const [priceMap, setPriceMap] = useState({});
  // Settings
  const [murekaKey, setMurekaKey] = useState('');
  const [gptContext, setGptContext] = useState('');
  const [settings, setSettings] = useState({});
  // Genre detail view
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [genreDetail, setGenreDetail] = useState(null);
  const [genreLoading, setGenreLoading] = useState(false);
  const [playingTrackId, setPlayingTrackId] = useState(null);
  const audioRef = React.useRef(null);

  const loadStats = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/production/inventory/stats`, { withCredentials: true }); setStats(r.data); } catch {}
  }, []);
  const loadRequests = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/production/requests`, { withCredentials: true }); setRequests(r.data); } catch {}
  }, []);
  const loadTracks = useCallback(async () => {
    try {
      let url = `${API}/api/production/inventory/tracks?limit=100`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterGenre) url += `&genre=${encodeURIComponent(filterGenre)}`;
      const r = await axios.get(url, { withCredentials: true });
      setTracks(r.data.tracks || []); setTracksTotal(r.data.total || 0);
    } catch {}
  }, [filterStatus, filterGenre]);
  const loadBatches = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/production/batches`, { withCredentials: true }); setBatches(r.data); } catch {}
    try { const r = await axios.get(`${API}/api/production/batch-summary`, { withCredentials: true }); setBatchSummary(r.data); } catch {}
    try { const r = await axios.get(`${API}/api/production/pending-by-genre`, { withCredentials: true }); setPendingByGenre(r.data); } catch {}
  }, []);
  const loadSettings = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/production/settings`, { withCredentials: true }); setSettings(r.data); } catch {}
  }, []);

  useEffect(() => { loadStats(); loadRequests(); }, []);
  useEffect(() => {
    if (tab === 'mastering') loadBatches();
    if (tab === 'settings') loadSettings();
    if (tab === 'inventory') loadTracks();
  }, [tab, filterStatus, filterGenre]);

  // Auto-refresh batches every 10s on mastering tab (real-time updates)
  useEffect(() => {
    if (tab !== 'mastering') return;
    const interval = setInterval(() => { loadBatches(); }, 10000);
    return () => clearInterval(interval);
  }, [tab]);

  // Auto-detect generating request on load
  useEffect(() => {
    const gen = requests.find(r => r.status === 'generating');
    if (gen && !generatingReqId) setGeneratingReqId(gen.id);
  }, [requests]);

  // ─── Actions ───
  const submitRequest = async () => {
    if (!reqGenre) return;
    const names = reqTrackNames.split('\n').map(n => n.trim()).filter(n => n);
    const qty = names.length > 0 ? names.length : reqQuantity;
    setActionLoading('submit');
    try {
      await axios.post(`${API}/api/production/request`, {
        genres: [{ genre: reqGenre, quantity: qty, bpm_min: reqBpmMin, bpm_max: reqBpmMax, vibe: reqVibe, notes: reqNotes }],
        notes: reqNotes,
        language: reqLanguage,
        track_names: names,
      }, { withCredentials: true });
      setShowNewRequest(false);
      setReqGenre(''); setReqQuantity(20); setReqVibe(''); setReqNotes(''); setReqTrackNames(''); setReqLanguage('Espanol');
      loadRequests(); loadStats();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setActionLoading(null);
  };

  const approveRequest = async (id, approved) => {
    setActionLoading(id);
    try { await axios.post(`${API}/api/production/approve/${id}?approved=${approved}`, {}, { withCredentials: true }); loadRequests(); } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setActionLoading(null);
  };

  const startGeneration = async (id) => {
    setActionLoading(id);
    try {
      await axios.post(`${API}/api/production/generate-prompts/${id}`, {}, { withCredentials: true });
      setGeneratingReqId(id);
      loadRequests();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setActionLoading(null);
  };

  // Poll generation progress
  useEffect(() => {
    if (!generatingReqId) return;
    const interval = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/production/request-progress/${generatingReqId}`, { withCredentials: true });
        setGenProgress(r.data);
        if (r.data.status === 'completed' || r.data.status === 'failed' || r.data.status === 'error') {
          setGeneratingReqId(null);
          loadRequests(); loadStats(); loadBatches();
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [generatingReqId]);

  const downloadBatchById = async (batchId) => {
    setActionLoading('dl_' + batchId);
    try {
      const r = await axios.post(`${API}/api/production/download-batch-id/${batchId}`, {}, { withCredentials: true, responseType: 'blob', timeout: 300000 });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `OG_MUSIC_${batchId}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error descargando el lote. Intenta de nuevo.');
    }
    setActionLoading(null);
  };

  // Detect if showDirectoryPicker is available AND usable (not blocked by iframe sandbox)
  const canPickDirectory = () => {
    try {
      if (typeof window.showDirectoryPicker !== 'function') return false;
      // Cross-origin iframes block showDirectoryPicker for security
      if (window.self !== window.top) return false;
      return true;
    } catch { return false; }
  };

  // Smart download: try directory picker first, fallback to server ZIP
  const downloadBatchSmart = async (batch) => {
    if (canPickDirectory()) {
      try {
        const dirHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
        setActionLoading('dl_' + batch.batch_id);
        let downloaded = 0;
        for (const t of (batch.tracks || [])) {
          if (!t.mp3_url) continue;
          try {
            const resp = await fetch(t.mp3_url);
            const blob = await resp.blob();
            const fileName = `${t.title || 'track'}.mp3`.replace(/[/\\?%*:|"<>]/g, '_');
            const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
            downloaded++;
          } catch (err) { console.error('Error descargando track:', t.title, err); }
        }
        setActionLoading(null);
        alert(`${downloaded}/${batch.total} tracks guardados en la carpeta seleccionada`);
        return;
      } catch (e) {
        setActionLoading(null);
        if (e.name === 'AbortError') return; // User cancelled
        // Fall through to ZIP download
        console.warn('showDirectoryPicker failed, falling back to ZIP', e);
      }
    }
    // Fallback: server-side ZIP download (always works)
    await downloadBatchById(batch.batch_id);
  };

  const downloadBatch = async (genre) => {
    const loadingKey = genre ? `dl_${genre}` : 'download';
    setActionLoading(loadingKey);
    try {
      const r = await axios.post(`${API}/api/production/download-batch`, { genre: genre || null }, {
        withCredentials: true, responseType: 'blob', timeout: 180000
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `OG_MUSIC_${(genre || 'Mix').replace(/\s/g, '_')}_mastering.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      loadBatches();
    } catch (e) {
      const errMsg = e.response?.data ? (typeof e.response.data === 'string' ? e.response.data : 'Error descargando') : 'No hay tracks para descargar';
      alert(errMsg);
    }
    setActionLoading(null);
  };

  const uploadBatchFiles = async (batchId) => {
    const files = batchUploadFiles[batchId];
    if (!files?.length) return;
    setBatchUploading(batchId);
    setBatchUploadProgress({ done: 0, total: files.length });
    let okCount = 0;
    const errors = [];
    try {
      // Upload files ONE BY ONE to avoid Cloudflare/proxy 100MB limit
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fd = new FormData();
        fd.append('files', file);
        try {
          const r = await axios.post(`${API}/api/production/upload-mastered-batch`, fd, {
            withCredentials: true,
            timeout: 300000,
          });
          const result = r.data.results?.[0];
          if (result?.status === 'ok') {
            okCount += 1;
          } else {
            errors.push(`${file.name}: ${result?.detail || 'no encontrado'}`);
          }
        } catch (err) {
          errors.push(`${file.name}: ${err.response?.data?.detail || err.message || 'error de red'}`);
        }
        setBatchUploadProgress({ done: i + 1, total: files.length });
      }
      if (errors.length === 0) {
        alert(`✓ ${okCount}/${files.length} WAV subidos exitosamente`);
      } else {
        alert(`${okCount}/${files.length} WAV subidos.\n\nErrores:\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n...(+${errors.length - 5} más)` : ''}`);
      }
      setBatchUploadFiles(prev => ({ ...prev, [batchId]: null }));
      loadBatches(); loadStats();
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Error');
    }
    setBatchUploading(null);
    setBatchUploadProgress({ done: 0, total: 0 });
  };

  const assignPrices = async () => {
    const items = Object.entries(priceMap).filter(([, v]) => v > 0).map(([id, price]) => ({ track_id: id, price: parseFloat(price) }));
    if (items.length === 0) return;
    setActionLoading('prices');
    try {
      await axios.post(`${API}/api/production/assign-prices`, { tracks: items }, { withCredentials: true });
      setPriceMap({});
      alert(`Envio exitoso! ${items.length} tracks enviados a la espera de aprobacion por administracion.`);
      loadTracks(); loadStats(); loadBatches();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setActionLoading(null);
  };

  const publishTracks = async (trackIds) => {
    setActionLoading('publish');
    try { await axios.post(`${API}/api/production/publish-approved`, trackIds, { withCredentials: true }); loadTracks(); loadStats(); } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setActionLoading(null);
  };

  const saveMurekaKey = async () => {
    try { await axios.post(`${API}/api/production/settings/mureka`, { api_key: murekaKey }, { withCredentials: true }); loadSettings(); alert('Guardada'); } catch {}
  };
  const saveGptContext = async () => {
    try { await axios.post(`${API}/api/production/settings/gpt-context`, { context: gptContext }, { withCredentials: true }); loadSettings(); alert('Guardado'); } catch {}
  };

  // Genre detail
  const openGenreDetail = async (genre) => {
    setSelectedGenre(genre);
    setGenreLoading(true);
    try {
      const r = await axios.get(`${API}/api/production/genre-detail/${encodeURIComponent(genre)}`, { withCredentials: true });
      setGenreDetail(r.data);
    } catch { setGenreDetail(null); }
    setGenreLoading(false);
  };

  const closeGenreDetail = () => {
    setSelectedGenre(null);
    setGenreDetail(null);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlayingTrackId(null);
  };

  const togglePlay = (trackId, url) => {
    if (!url) return;
    if (playingTrackId === trackId) {
      audioRef.current?.pause();
      setPlayingTrackId(null);
    } else {
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(url);
      audio.play();
      audio.onended = () => setPlayingTrackId(null);
      audioRef.current = audio;
      setPlayingTrackId(trackId);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'requests', label: 'Solicitudes', icon: Send },
    { id: 'mastering', label: 'Mastering', icon: Music },
    { id: 'inventory', label: 'Inventario', icon: Package },
    ...(isAdmin ? [{ id: 'settings', label: 'Config', icon: Settings }] : []),
  ];

  // Genre counts from stats
  const genreCounts = stats?.published?.by_genre || {};
  const inventoryGenreCounts = {};
  if (stats?.inventory) {
    Object.values(stats.inventory).forEach(genres => {
      Object.entries(genres).forEach(([g, c]) => { inventoryGenreCounts[g] = (inventoryGenreCounts[g] || 0) + c; });
    });
  }

  return (
    <div data-testid="production-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Produccion Musical</h2>
        <button onClick={() => { loadStats(); loadRequests(); loadBatches(); }} className="p-2 hover:bg-white/10 rounded-lg text-white/40"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white/[0.03] p-1 rounded-xl" data-testid="production-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${tab === t.id ? 'bg-[#E8720C] text-white' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      {/* ═══ DASHBOARD ═══ */}
      {tab === 'dashboard' && stats && (
        <div className="space-y-4">
          {/* Mureka Balance */}
          {stats.mureka && (
            <div className={`glass-card p-4 border ${
              stats.mureka.estimated_remaining !== null && stats.mureka.estimated_remaining < 20 ? 'border-red-500/30' :
              stats.mureka.estimated_remaining !== null && stats.mureka.estimated_remaining < 100 ? 'border-yellow-500/30' : 'border-[#E8720C]/20'
            }`} data-testid="mureka-balance">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#E8720C]/15 flex items-center justify-center">
                    <Music className="w-5 h-5 text-[#E8720C]" />
                  </div>
                  <div>
                    <p className="text-xs text-white/40 uppercase tracking-wider">Mureka API</p>
                    <p className="text-lg font-bold text-white">
                      {stats.mureka.balance?.balance !== undefined ? `$${stats.mureka.balance.balance.toFixed(2)}` : 'Sin datos'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {stats.mureka.estimated_remaining !== null && (
                    <p className={`text-2xl font-bold ${
                      stats.mureka.estimated_remaining < 20 ? 'text-red-400' :
                      stats.mureka.estimated_remaining < 100 ? 'text-yellow-400' : 'text-green-400'
                    }`}>{stats.mureka.estimated_remaining}</p>
                  )}
                  <p className="text-[10px] text-white/40">canciones restantes</p>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/5 text-[10px] text-white/30">
                <span>Generadas: <strong className="text-white/60">{stats.mureka.total_generated}</strong></span>
                <span>Costo/cancion: <strong className="text-white/60">${stats.mureka.cost_per_song}</strong></span>
                <span>Gastado: <strong className="text-white/60">~${stats.mureka.estimated_spent}</strong></span>
                {stats.mureka.estimated_remaining !== null && stats.mureka.estimated_remaining < 50 && (
                  <span className="text-yellow-400 font-bold ml-auto">Recarga pronto en platform.mureka.ai</span>
                )}
              </div>
            </div>
          )}

          {/* Top stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="glass-card p-4 text-center">
              <p className="text-2xl font-bold text-[#E8720C]">{stats.published?.total || 0}</p>
              <p className="text-[10px] text-white/40 uppercase">Publicados</p>
              <p className="text-[9px] text-white/20 mt-1">Limite: 1,000</p>
            </div>
            <div className="glass-card p-4 text-center">
              <p className="text-2xl font-bold text-green-400">{stats.slots_available}</p>
              <p className="text-[10px] text-white/40 uppercase">Espacios Libres</p>
            </div>
            <div className={`glass-card p-4 text-center ${stats.can_publish_more ? '' : 'border border-red-500/30'}`}>
              <p className={`text-2xl font-bold ${stats.can_publish_more ? 'text-blue-400' : 'text-red-400'}`}>{stats.can_publish_more ? 'OK' : 'LLENO'}</p>
              <p className="text-[10px] text-white/40 uppercase">Catalogo</p>
            </div>
            <div className="glass-card p-4 text-center">
              <p className="text-2xl font-bold text-purple-400">
                {Object.values(stats.inventory || {}).reduce((s, g) => s + Object.values(g).reduce((a, b) => a + b, 0), 0)}
              </p>
              <p className="text-[10px] text-white/40 uppercase">En Pipeline</p>
            </div>
          </div>

          {/* Genre breakdown — ALL genres, clickable */}
          <div className="glass-card p-4">
            <h3 className="text-sm font-semibold mb-3">Temas por Genero <span className="text-[10px] text-white/30 font-normal ml-2">(haz clic para ver y escuchar)</span></h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {GENRES.map(genre => {
                const pubCount = genreCounts[genre] || 0;
                const pipeCount = inventoryGenreCounts[genre] || 0;
                const total = pubCount + pipeCount;
                return (
                  <button key={genre} onClick={() => openGenreDetail(genre)}
                    className={`p-3 rounded-xl text-left transition-all hover:scale-[1.02] ${
                      total > 0 ? 'bg-white/[0.04] hover:bg-[#E8720C]/10 border border-white/5 hover:border-[#E8720C]/20' : 'bg-white/[0.015] hover:bg-white/[0.04] border border-white/[0.03]'
                    }`} data-testid={`genre-card-${genre}`}>
                    <p className="text-xs font-medium text-white/70 truncate">{genre}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {pubCount > 0 && <span className="text-xs font-bold text-green-400">{pubCount} <span className="text-[8px] font-normal text-green-400/60">en venta</span></span>}
                      {pipeCount > 0 && <span className="text-xs font-bold text-purple-400">{pipeCount} <span className="text-[8px] font-normal text-purple-400/60">en proceso</span></span>}
                      {total === 0 && <span className="text-[10px] text-white/20">Sin temas</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ═══ GENRE DETAIL MODAL ═══ */}
      {selectedGenre && (
        <div className="fixed inset-0 bg-black/90 z-[60] flex flex-col" data-testid="genre-detail-modal">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[#0a0a0e] shrink-0">
            <div className="flex items-center gap-4">
              <button onClick={closeGenreDetail} className="p-2 rounded-lg hover:bg-white/10 text-white/50"><X className="w-5 h-5" /></button>
              <div>
                <h3 className="text-lg font-bold">{selectedGenre}</h3>
                {genreDetail && (
                  <div className="flex gap-3 text-xs mt-0.5">
                    <span className="text-white/30">{genreDetail.total} tracks total</span>
                    {genreDetail.counts?.publicado > 0 && <span className="text-green-400">{genreDetail.counts.publicado} en venta</span>}
                    {genreDetail.counts?.pendiente_aprobacion > 0 && <span className="text-orange-400">{genreDetail.counts.pendiente_aprobacion} pendientes</span>}
                    {genreDetail.counts?.masterizado > 0 && <span className="text-blue-400">{genreDetail.counts.masterizado} masterizados</span>}
                    {genreDetail.counts?.pendiente_mastering > 0 && <span className="text-yellow-400">{genreDetail.counts.pendiente_mastering} en mastering</span>}
                    {genreDetail.counts?.generado > 0 && <span className="text-purple-400">{genreDetail.counts.generado} generados</span>}
                  </div>
                )}
              </div>
            </div>
            <button onClick={closeGenreDetail} className="px-5 py-2 bg-white/10 hover:bg-white/15 rounded-xl text-sm font-bold text-white/70">Cerrar</button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {genreLoading ? (
              <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-[#E8720C]" /></div>
            ) : !genreDetail?.tracks?.length ? (
              <div className="flex items-center justify-center h-full text-white/30 text-sm">No hay tracks en este genero</div>
            ) : (
              <div>
                {/* Group by status */}
                {['publicado', 'pendiente_aprobacion', 'masterizado', 'pendiente_mastering', 'generado'].map(status => {
                  const statusTracks = genreDetail.tracks.filter(t => t.status === status);
                  if (statusTracks.length === 0) return null;
                  const sc = STATUS_LABELS[status] || { label: status, color: '' };
                  return (
                    <div key={status}>
                      <div className={`sticky top-0 z-10 px-6 py-2.5 border-b border-white/[0.06] flex items-center gap-3 ${
                        status === 'publicado' ? 'bg-green-500/5' : 'bg-[#111114]'
                      }`}>
                        <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${sc.color}`}>{sc.label}</span>
                        <span className="text-xs text-white/30">{statusTracks.length} tracks</span>
                        {status === 'publicado' && <span className="text-[10px] text-green-400">EN VENTA</span>}
                      </div>
                      {statusTracks.map((t, i) => (
                        <div key={t.id} className={`flex items-center gap-4 px-6 py-3.5 border-b border-white/[0.04] group transition-colors ${
                          i % 2 === 0 ? 'bg-transparent hover:bg-white/[0.03]' : 'bg-white/[0.015] hover:bg-white/[0.04]'
                        }`}>
                          {/* Play */}
                          {t.audio_url ? (
                            <button onClick={() => togglePlay(t.id, t.audio_url)}
                              className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#E8720C]/20 flex items-center justify-center shrink-0 transition-colors">
                              {playingTrackId === t.id ? <Pause className="w-4 h-4 text-[#E8720C]" /> : <Play className="w-4 h-4 text-white/50 ml-0.5" />}
                            </button>
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center shrink-0"><Music className="w-4 h-4 text-white/15" /></div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white/80 truncate">{t.title}</p>
                            <p className="text-[10px] text-white/30">{t.artist || 'OG MUSIC'} {t.vibe ? `| ${t.vibe}` : ''}</p>
                          </div>
                          <span className="text-xs text-[#E8720C] font-medium w-16 text-right shrink-0">{t.bpm > 0 ? `${t.bpm} BPM` : ''}</span>
                          {t.price > 0 && <span className="text-xs font-bold text-green-400 w-14 text-right shrink-0">${t.price}</span>}
                          <span className={`px-2 py-0.5 rounded text-[8px] font-bold shrink-0 ${sc.color}`}>{sc.label}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ SOLICITUDES (1 genero por solicitud) ═══ */}
      {tab === 'requests' && (
        <div className="space-y-4">
          {/* Genre summary — what we already have */}
          <div className="glass-card p-4">
            <h3 className="text-xs font-semibold text-white/50 mb-2 uppercase tracking-wider">Temas que ya tenemos (publicados + pipeline)</h3>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries({ ...genreCounts })
                .sort((a, b) => b[1] - a[1])
                .map(([genre, count]) => (
                  <span key={genre} className="px-2.5 py-1 bg-white/5 rounded-lg text-[10px] text-white/50">
                    {genre}: <strong className="text-[#E8720C]">{count}</strong>
                    {inventoryGenreCounts[genre] ? <span className="text-purple-400 ml-1">+{inventoryGenreCounts[genre]}</span> : ''}
                  </span>
                ))}
            </div>
          </div>

          <button onClick={() => setShowNewRequest(!showNewRequest)}
            className="px-4 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-xl text-sm font-bold flex items-center gap-2"
            data-testid="new-production-request">
            <Plus className="w-4 h-4" /> Nueva Solicitud
          </button>

          {showNewRequest && (
            <div className="glass-card p-5 space-y-4 border border-[#E8720C]/20" data-testid="new-request-form">
              <h3 className="text-sm font-semibold">Solicitar Produccion</h3>

              {/* Genre selector */}
              <div>
                <label className="text-[10px] text-white/40 uppercase tracking-wider mb-1.5 block">Genero</label>
                <div className="flex flex-wrap gap-1.5 max-h-[200px] overflow-y-auto p-2 bg-black/20 rounded-xl">
                  {GENRES.map(g => (
                    <button key={g} onClick={() => setReqGenre(g)}
                      className={`px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
                        reqGenre === g
                          ? 'bg-[#E8720C] text-white ring-1 ring-[#E8720C]/50'
                          : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80'
                      }`}>
                      {g}
                      {genreCounts[g] ? <span className="ml-1 text-[8px] opacity-60">({genreCounts[g]})</span> : ''}
                    </button>
                  ))}
                </div>
                {reqGenre && <p className="text-xs text-[#E8720C] mt-2 font-medium">Seleccionado: {reqGenre}</p>}
              </div>

              {/* Language */}
              <div>
                <label className="text-[10px] text-white/40 uppercase tracking-wider mb-1.5 block">Idioma de las Letras</label>
                <div className="flex gap-2">
                  {['Espanol', 'Ingles', 'Portugues', 'Frances', 'Yoruba', 'Swahili', 'Zulu', 'Amharic', 'Afrikaans', 'Creole Haitiano', 'Espanol + Ingles', 'Ingles + Yoruba', 'Frances + Yoruba'].map(lang => (
                    <button key={lang} onClick={() => setReqLanguage(lang)}
                      className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                        reqLanguage === lang
                          ? 'bg-[#E8720C] text-white'
                          : 'bg-white/5 text-white/50 hover:bg-white/10'
                      }`}>{lang}</button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] text-white/40">Temas por lote</label>
                  <div className="flex gap-1.5 mt-1">
                    {[1, 2, 3, 5, 10, 15, 20].map(n => (
                      <button key={n} onClick={() => setReqQuantity(n)}
                        className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                          reqQuantity === n ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'
                        }`}>{n}</button>
                    ))}
                  </div>
                  <p className="text-[9px] text-white/25 mt-1">Se generan de forma secuencial (1 interaccion). ~2 min por tema.</p>
                </div>
                <div>
                  <label className="text-[10px] text-white/40">BPM Min</label>
                  <input type="number" value={reqBpmMin} onChange={e => setReqBpmMin(parseInt(e.target.value) || 80)}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#E8720C]/50" />
                </div>
                <div>
                  <label className="text-[10px] text-white/40">BPM Max</label>
                  <input type="number" value={reqBpmMax} onChange={e => setReqBpmMax(parseInt(e.target.value) || 160)}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#E8720C]/50" />
                </div>
                <div>
                  <label className="text-[10px] text-white/40">Vibe / Estilo</label>
                  <input value={reqVibe} onChange={e => setReqVibe(e.target.value)} placeholder="Dark, Melodico, Chill..."
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#E8720C]/50" />
                </div>
              </div>

              {/* Track Names */}
              <div>
                <label className="text-[10px] text-white/40 uppercase tracking-wider mb-1.5 block">Nombres de las Canciones (1 por linea)</label>
                <textarea value={reqTrackNames} onChange={e => {
                    setReqTrackNames(e.target.value);
                    const count = e.target.value.split('\n').filter(n => n.trim()).length;
                    if (count > 0) setReqQuantity(count);
                  }}
                  placeholder={"Noche Sin Final\nFuego en la Piel\nBailando Contigo\nTu Mirada\nEstrella Fugaz\n..."}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/20 resize-none h-40 focus:outline-none focus:border-[#E8720C]/50 font-mono" />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-[9px] text-white/25">Escribe los nombres que quieras para tus canciones. La cantidad se ajusta automaticamente.</p>
                  <span className="text-xs font-bold text-[#E8720C]">{reqTrackNames.split('\n').filter(n => n.trim()).length || 0} nombres</span>
                </div>
              </div>

              <div>
                <label className="text-[10px] text-white/40">Notas (ayudan al prompt de generacion)</label>
                <textarea value={reqNotes} onChange={e => setReqNotes(e.target.value)}
                  placeholder="Ej: Quiero que suene como Bad Bunny, con 808s fuertes y hi-hats rapidos. Que sea para perreo intenso..."
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/20 resize-none h-20 focus:outline-none focus:border-[#E8720C]/50" />
                <p className="text-[9px] text-white/25 mt-1">Estas notas se usan directamente en el prompt de la AI para generar exactamente lo que quieres</p>
              </div>

              <div className="flex gap-2">
                <button onClick={submitRequest} disabled={!reqGenre || actionLoading === 'submit'}
                  className="px-6 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
                  {actionLoading === 'submit' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  Enviar Solicitud: {reqTrackNames.split('\n').filter(n => n.trim()).length || reqQuantity}x {reqGenre || '...'} ({reqLanguage}) ~{(reqTrackNames.split('\n').filter(n => n.trim()).length || reqQuantity) * 2} min
                </button>
                <button onClick={() => setShowNewRequest(false)} className="px-5 py-2 text-white/40 text-xs">Cancelar</button>
              </div>
            </div>
          )}

          {/* Requests list */}
          {requests.map(req => (
            <div key={req.id} className={`glass-card p-4 border ${req.status === 'pending' ? 'border-yellow-500/20' : req.status === 'approved' ? 'border-green-500/20' : req.status === 'generating' ? 'border-[#E8720C]/30' : 'border-white/5'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${
                      req.status === 'approved' ? 'bg-blue-500/15 text-blue-400' :
                      req.status === 'generating' ? 'bg-[#E8720C]/15 text-[#E8720C]' :
                      req.status === 'completed' ? 'bg-green-500/15 text-green-400' :
                      (req.status === 'failed' || req.status === 'error') ? 'bg-red-500/15 text-red-400' :
                      'bg-yellow-500/15 text-yellow-400'
                    }`}>{req.status === 'approved' ? 'En cola' : req.status === 'generating' ? 'Generando...' : req.status === 'completed' ? 'Completada' : (req.status === 'failed' || req.status === 'error') ? 'ERROR' : req.status}</span>
                    <span className="text-[10px] text-white/30">{new Date(req.created_at).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-white">{req.total_tracks} temas</p>
                    {req.genres?.map((g, i) => (
                      <span key={i} className="px-2 py-0.5 bg-[#E8720C]/10 text-[#E8720C] rounded text-[10px] font-medium">
                        {g.genre} {g.vibe ? `(${g.vibe})` : ''} | {g.bpm_min}-{g.bpm_max} BPM
                      </span>
                    ))}
                  </div>
                  {req.notes && <p className="text-[10px] text-white/30 mt-1 italic">{req.notes}</p>}
                  {req.status === 'completed' && req.tracks_generated > 0 && (
                    <div className="flex items-center gap-3 mt-1">
                      <p className="text-[10px] text-green-400">{req.tracks_generated} tracks generados</p>
                      <button onClick={async () => {
                        if (!window.confirm('Eliminar esta solicitud completada del historial?')) return;
                        try { await axios.delete(`${API}/api/production/request/${req.id}`, { withCredentials: true }); loadRequests(); } catch (e) { alert(e.response?.data?.detail || 'Error'); }
                      }} className="px-2 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded text-[9px] font-bold flex items-center gap-1" data-testid={`delete-completed-${req.id}`}>
                        <Trash2 className="w-2.5 h-2.5" /> Eliminar
                      </button>
                    </div>
                  )}
                  {req.status === 'completed' && req.tracks_generated === 0 && (
                    <div className="mt-2">
                      <p className="text-[10px] text-red-400">0 tracks generados - GPT creo los prompts pero Mureka no genero audio</p>
                      <div className="flex gap-2 mt-2">
                        <button onClick={async () => {
                          try {
                            await axios.post(`${API}/api/production/retry/${req.id}`, {}, { withCredentials: true });
                            loadRequests();
                          } catch (e) { alert(e.response?.data?.detail || 'Error'); }
                        }} className="px-3 py-1.5 bg-[#E8720C]/15 hover:bg-[#E8720C]/25 text-[#E8720C] rounded-lg text-[10px] font-bold flex items-center gap-1">
                          <RefreshCw className="w-3 h-3" /> Reintentar
                        </button>
                        <button onClick={async () => {
                          if (!window.confirm('Eliminar esta solicitud?')) return;
                          try { await axios.delete(`${API}/api/production/request/${req.id}`, { withCredentials: true }); loadRequests(); } catch (e) { alert(e.response?.data?.detail || 'Error'); }
                        }} className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-[10px] font-bold flex items-center gap-1">
                          <Trash2 className="w-3 h-3" /> Eliminar
                        </button>
                      </div>
                    </div>
                  )}
                  {req.status === 'generating' && (
                    <div className="mt-2">
                      <button onClick={async () => {
                        if (!window.confirm('Cancelar esta solicitud en proceso?')) return;
                        try {
                          await axios.post(`${API}/api/production/cancel/${req.id}`, {}, { withCredentials: true });
                          loadRequests();
                        } catch (e) { alert(e.response?.data?.detail || 'Error'); }
                      }} className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-[10px] font-bold flex items-center gap-1"
                        data-testid={`cancel-req-${req.id}`}>
                        <XCircle className="w-3 h-3" /> Detener / Cancelar
                      </button>
                    </div>
                  )}
                  {(req.status === 'failed' || req.status === 'error') && (
                    <div className="mt-2">
                      <p className="text-[10px] text-red-400">{req.generation_log || 'Error en la generacion'}</p>
                      <div className="flex gap-2 mt-2">
                        <button onClick={async () => {
                          try {
                            await axios.post(`${API}/api/production/retry/${req.id}`, {}, { withCredentials: true });
                            loadRequests();
                          } catch (e) { alert(e.response?.data?.detail || 'Error'); }
                        }} className="px-3 py-1.5 bg-[#E8720C]/15 hover:bg-[#E8720C]/25 text-[#E8720C] rounded-lg text-[10px] font-bold flex items-center gap-1"
                          data-testid={`retry-req-${req.id}`}>
                          <RefreshCw className="w-3 h-3" /> Reintentar
                        </button>
                        <button onClick={async () => {
                          if (!window.confirm('Eliminar esta solicitud con error?')) return;
                          try {
                            await axios.delete(`${API}/api/production/request/${req.id}`, { withCredentials: true });
                            loadRequests();
                          } catch (e) { alert(e.response?.data?.detail || 'Error'); }
                        }} className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-[10px] font-bold flex items-center gap-1"
                          data-testid={`delete-req-${req.id}`}>
                          <Trash2 className="w-3 h-3" /> Eliminar
                        </button>
                      </div>
                    </div>
                  )}
                  <p className="text-[9px] text-white/20 mt-1">Por: {req.requested_by_name || req.requested_by}</p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  {req.status === 'generating' && <Loader2 className="w-5 h-5 animate-spin text-[#E8720C]" />}
                </div>
              </div>
            </div>
          ))}
          {requests.length === 0 && <p className="text-center text-white/30 py-8 text-sm">No hay solicitudes</p>}

          {/* Real-time generation progress */}
          {generatingReqId && genProgress && (
            <div className="glass-card p-5 border border-[#E8720C]/30 animate-pulse-slow" data-testid="generation-progress">
              <div className="flex items-center gap-3 mb-3">
                <Loader2 className="w-5 h-5 animate-spin text-[#E8720C]" />
                <div>
                  <h3 className="text-sm font-semibold text-[#E8720C]">Produccion en Proceso</h3>
                  <p className="text-[10px] text-white/40">{genProgress.generation_log}</p>
                </div>
                <span className="ml-auto text-sm font-bold text-white">{genProgress.tracks?.length || 0}/{genProgress.total_requested}</span>
              </div>
              {/* Progress bar */}
              <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-[#E8720C] rounded-full transition-all" style={{ width: `${genProgress.total_requested > 0 ? ((genProgress.tracks?.length || 0) / genProgress.total_requested) * 100 : 0}%` }} />
              </div>
              {/* Tracks appearing in real time */}
              <div className="space-y-1">
                {genProgress.tracks?.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-green-500/5 border border-green-500/10 rounded-lg animate-fade-in">
                    <CheckCircle className="w-3 h-3 text-green-400 shrink-0" />
                    <span className="text-xs text-white/70 font-medium">{t.title}</span>
                    <span className="text-[10px] text-white/30">{t.genre}</span>
                    <span className="text-[10px] text-[#E8720C]">{t.bpm} BPM</span>
                    <span className="text-[10px] text-white/20">{t.vibe}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ MASTERING — Casillas por lote ═══ */}
      {tab === 'mastering' && (
        <div className="space-y-4">
          {/* Pipeline counters */}
          {batchSummary?.counters && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {[
                { label: 'Listos', value: batchSummary.counters.listos_para_descargar, color: 'text-purple-400', sub: 'Para descargar' },
                { label: 'En Mastering', value: batchSummary.counters.en_mastering, color: 'text-yellow-400', sub: 'Mastering OG' },
                { label: 'Masterizados', value: batchSummary.counters.masterizados_sin_precio, color: 'text-blue-400', sub: 'Sin precio' },
                { label: 'Pendientes', value: batchSummary.counters.pendientes_aprobacion, color: 'text-orange-400', sub: 'Con precio' },
                { label: 'Catalogo', value: `${batchSummary.counters.publicados_catalogo}/1000`, color: 'text-green-400', sub: `${batchSummary.counters.espacios_disponibles} libres` },
              ].map((c, i) => (
                <div key={i} className="glass-card p-3 text-center">
                  <p className={`text-xl font-bold ${c.color}`}>{c.value}</p>
                  <p className="text-[10px] text-white/50">{c.label}</p>
                  <p className="text-[9px] text-white/25">{c.sub}</p>
                </div>
              ))}
            </div>
          )}

          {/* Download new batch - separated by genre */}
          {batchSummary?.counters?.listos_para_descargar > 0 && (
            <div className="glass-card p-4 border border-purple-500/20">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold flex items-center gap-2"><Download className="w-4 h-4 text-purple-400" /> Lotes para Masterizar</h3>
                  <p className="text-xs text-white/40 mt-1">{batchSummary.counters.listos_para_descargar} tracks listos</p>
                </div>
                <button onClick={() => downloadBatch('')} disabled={actionLoading === 'download'}
                  className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white/50 rounded-lg text-xs font-medium flex items-center gap-1.5 disabled:opacity-50">
                  {actionLoading === 'download' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                  Descargar Todo Junto
                </button>
              </div>
              {/* Genre cards for separate downloads */}
              {pendingByGenre.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {pendingByGenre.map(g => (
                    <button key={g.genre} onClick={() => downloadBatch(g.genre)}
                      disabled={actionLoading === `dl_${g.genre}`}
                      className="p-3 bg-purple-500/5 hover:bg-purple-500/10 border border-purple-500/15 hover:border-purple-500/30 rounded-xl text-left transition-all disabled:opacity-50"
                      data-testid={`download-genre-${g.genre}`}>
                      <p className="text-sm font-bold text-purple-400">{g.genre}</p>
                      <p className="text-[10px] text-white/30">{g.count} tracks</p>
                      {actionLoading === `dl_${g.genre}` && <Loader2 className="w-3 h-3 animate-spin text-purple-400 mt-1" />}
                    </button>
                  ))}
                </div>
              )}
              {pendingByGenre.length === 0 && (
                <div className="flex flex-wrap gap-2">
                  {batchSummary?.ready_by_genre && Object.entries(batchSummary.ready_by_genre).map(([genre, count]) => (
                    <button key={genre} onClick={() => downloadBatch(genre)} disabled={actionLoading === 'download'}
                      className="px-3 py-2 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 rounded-lg text-xs font-medium">
                      {genre} ({count})
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Batch cards */}
          {batches.length === 0 && !batchSummary?.counters?.listos_para_descargar && (
            <p className="text-center text-white/30 py-12 text-sm">No hay lotes. Genera tracks en Solicitudes primero.</p>
          )}

          {batches.map(batch => {
            const isExpanded = expandedBatch === batch.batch_id;
            const pct = batch.total > 0 ? Math.round((batch.returned / batch.total) * 100) : 0;
            const pendingTracks = batch.tracks?.filter(t => t.status === 'pendiente_mastering') || [];
            const masteredTracks = batch.tracks?.filter(t => ['masterizado', 'pendiente_aprobacion', 'publicado', 'archivo_empresa'].includes(t.status)) || [];
            const empresaTracks = batch.tracks?.filter(t => t.status === 'archivo_empresa') || [];
            const phase = BATCH_PHASE_STYLES[batch.batch_phase] || BATCH_PHASE_STYLES.in_progress;
            const isLocked = batch.all_sent || batch.all_published;

            return (
              <div key={batch.batch_id} className={`glass-card overflow-hidden border ${phase.border}`} data-testid={`batch-card-${batch.batch_id}`}>
                {/* Batch header — click to expand */}
                <button onClick={() => setExpandedBatch(isExpanded ? null : batch.batch_id)}
                  className={`w-full p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors text-left ${phase.bg}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${phase.icon_bg}`}>
                      {batch.all_published ? <CheckCircle className={`w-5 h-5 ${phase.icon_color}`} /> : batch.batch_phase === 'pending_approval' ? <Send className={`w-5 h-5 ${phase.icon_color}`} /> : <Music className={`w-5 h-5 ${phase.icon_color}`} />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{batch.genres?.join(', ')}</p>
                      <p className="text-[10px] text-white/30">{batch.batch_id} | {new Date(batch.sent_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-bold text-white">{batch.returned}/{batch.total}</p>
                      <p className="text-[9px] text-white/30">devueltos</p>
                    </div>
                    <div className="w-24 h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${batch.all_published ? 'bg-green-500' : batch.batch_phase === 'pending_approval' ? 'bg-blue-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${phase.badge_bg} ${phase.badge_color}`}>
                      {phase.label}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-white/30 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="border-t border-white/5 p-4 space-y-4 bg-black/20">
                    {/* Track list */}
                    <div className="space-y-1">
                      {batch.tracks?.map((t, i) => {
                        const sc = STATUS_LABELS[t.status] || STATUS_LABELS.generado;
                        return (
                          <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02]">
                            <span className="text-[10px] text-white/20 w-5 text-center">{i+1}</span>
                            <Music className="w-3.5 h-3.5 text-white/15 shrink-0" />
                            <span className="text-xs text-white/70 flex-1 truncate">{t.title}</span>
                            <span className="text-[10px] text-white/30">{t.genre}</span>
                            <span className="text-[10px] text-[#E8720C] font-medium w-16 text-right">{t.bpm > 0 ? `${t.bpm} BPM` : ''}</span>
                            {t.price > 0 && <span className="text-[10px] text-green-400 font-bold">${t.price}</span>}
                            <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${sc.color}`}>{sc.label}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Download ALL — LOCKED if all sent for approval */}
                    {!isLocked && (
                      <div className="p-3 bg-purple-500/5 border border-purple-500/15 rounded-xl flex items-center justify-between gap-2">
                        <div>
                          <p className="text-xs text-purple-400 font-semibold">Descargar todo el lote</p>
                          <p className="text-[10px] text-white/30">{batch.total} tracks MP3 — se descargan como ZIP (o a carpeta si tu navegador lo permite)</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => downloadBatchById(batch.batch_id)}
                            disabled={actionLoading === 'dl_' + batch.batch_id}
                            data-testid={`batch-download-zip-${batch.batch_id}`}
                            className="px-3 py-2 bg-white/5 hover:bg-white/10 text-white/70 rounded-lg text-[11px] font-medium flex items-center gap-1.5 disabled:opacity-50"
                            title="Descargar como archivo ZIP">
                            <Download className="w-3 h-3" /> ZIP
                          </button>
                          <button
                            onClick={() => downloadBatchSmart(batch)}
                            disabled={actionLoading === 'dl_' + batch.batch_id}
                            data-testid={`batch-download-${batch.batch_id}`}
                            className="px-4 py-2 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
                            {actionLoading === 'dl_' + batch.batch_id ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Descargando...</> : <><Download className="w-3.5 h-3.5" /> Descargar {batch.total} Tracks</>}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* LOCKED notice when sent for approval */}
                    {isLocked && !batch.all_published && (
                      <div className="p-3 bg-blue-500/5 border border-blue-500/15 rounded-xl flex items-center gap-3">
                        <Send className="w-5 h-5 text-blue-400 shrink-0" />
                        <div>
                          <p className="text-xs text-blue-400 font-semibold">Enviado a Aprobacion</p>
                          <p className="text-[10px] text-white/30">Este lote esta pendiente de aprobacion por administracion. Pasa al siguiente lote.</p>
                        </div>
                      </div>
                    )}

                    {/* Upload WAVs for this batch */}
                    {!isLocked && pendingTracks.length > 0 && (
                      <div className="p-3 bg-yellow-500/5 border border-yellow-500/15 rounded-xl">
                        <p className="text-xs text-yellow-400 font-semibold mb-2">{pendingTracks.length} tracks esperando WAV masterizado</p>
                        <input type="file" multiple accept=".wav,.mp3"
                          onChange={e => setBatchUploadFiles(prev => ({ ...prev, [batch.batch_id]: Array.from(e.target.files) }))}
                          className="block w-full text-xs text-white/50 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-[10px] file:font-bold file:bg-white/10 file:text-white/70" />
                        {batchUploadFiles[batch.batch_id]?.length > 0 && (
                          <button onClick={() => uploadBatchFiles(batch.batch_id)} disabled={batchUploading === batch.batch_id}
                            className="mt-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-[10px] font-bold flex items-center gap-1.5 disabled:opacity-50">
                            {batchUploading === batch.batch_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                            {batchUploading === batch.batch_id
                              ? `Subiendo ${batchUploadProgress.done}/${batchUploadProgress.total}…`
                              : `Subir ${batchUploadFiles[batch.batch_id].length} WAV`}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Assign prices for mastered tracks — only if NOT locked */}
                    {!isLocked && masteredTracks.filter(t => t.status === 'masterizado').length > 0 && (
                      <div className="p-3 bg-blue-500/5 border border-blue-500/15 rounded-xl">
                        <p className="text-xs text-blue-400 font-semibold mb-2">Tracks masterizados — Decide su destino</p>
                        <div className="space-y-1.5">
                          {batch.tracks?.filter(t => t.status === 'masterizado').map(t => (
                            <div key={t.id} className="flex items-center gap-2">
                              <span className="text-xs text-white/60 flex-1 truncate">{t.title}</span>
                              <span className="text-[10px] text-white/30">{t.genre}</span>
                              <span className="text-[10px] text-white/30">$</span>
                              <input type="number" step="0.01" placeholder="5.99"
                                value={priceMap[t.id] || ''}
                                onChange={e => setPriceMap(prev => ({ ...prev, [t.id]: e.target.value }))}
                                className="w-20 px-2 py-1 bg-white/5 border border-white/10 rounded text-xs text-white text-center focus:outline-none focus:border-[#E8720C]/50" />
                              <label className="flex items-center gap-1 text-[9px] text-white/40">
                                <input type="checkbox" checked={batchPlaylist[t.id] || false}
                                  onChange={e => setBatchPlaylist(prev => ({ ...prev, [t.id]: e.target.checked }))}
                                  className="rounded" />
                                Playlist
                              </label>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button onClick={assignPrices} disabled={actionLoading === 'prices'}
                            className="px-4 py-2 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-lg text-[10px] font-bold flex items-center gap-1.5 disabled:opacity-50"
                            data-testid="send-approval-btn">
                            <DollarSign className="w-3 h-3" /> Para Venta (Enviar a Aprobacion)
                          </button>
                          <button onClick={async () => {
                            const ids = batch.tracks.filter(t => t.status === 'masterizado').map(t => t.id);
                            if (!ids.length) return;
                            if (!window.confirm(`Marcar ${ids.length} track(s) como archivo de empresa? No se publicaran para venta.`)) return;
                            setActionLoading('empresa_' + batch.batch_id);
                            try {
                              await axios.post(`${API}/api/production/mark-empresa`, { track_ids: ids }, { withCredentials: true });
                              loadBatches(); loadStats();
                            } catch (e) { alert(e.response?.data?.detail || 'Error'); }
                            setActionLoading(null);
                          }}
                            disabled={actionLoading === 'empresa_' + batch.batch_id}
                            className="px-4 py-2 bg-purple-500/15 hover:bg-purple-500/25 text-purple-400 rounded-lg text-[10px] font-bold flex items-center gap-1.5 disabled:opacity-50"
                            data-testid="mark-empresa-btn">
                            <Package className="w-3 h-3" /> Quedar en Empresa
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Empresa tracks — option to delete */}
                    {empresaTracks.length > 0 && (
                      <div className="p-3 bg-purple-500/5 border border-purple-500/15 rounded-xl">
                        <p className="text-xs text-purple-400 font-semibold mb-2">Archivo de Empresa ({empresaTracks.length} tracks)</p>
                        {empresaTracks.map(t => (
                          <div key={t.id} className="flex items-center gap-2 py-1">
                            <Package className="w-3 h-3 text-purple-400" />
                            <span className="text-xs text-white/70 flex-1">{t.title}</span>
                            <span className="text-[10px] text-white/30">{t.genre}</span>
                          </div>
                        ))}
                        <button onClick={async () => {
                          if (!window.confirm(`Eliminar ${empresaTracks.length} track(s) de archivo empresa?`)) return;
                          setActionLoading('delempresa_' + batch.batch_id);
                          try {
                            await axios.delete(`${API}/api/production/empresa-tracks`, {
                              data: { track_ids: empresaTracks.map(t => t.id) },
                              withCredentials: true,
                            });
                            loadBatches(); loadStats();
                          } catch (e) { alert(e.response?.data?.detail || 'Error'); }
                          setActionLoading(null);
                        }}
                          disabled={actionLoading === 'delempresa_' + batch.batch_id}
                          className="mt-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-[10px] font-bold flex items-center gap-1.5 disabled:opacity-50"
                          data-testid="delete-empresa-btn">
                          {actionLoading === 'delempresa_' + batch.batch_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          Eliminar del Sistema
                        </button>
                      </div>
                    )}

                    {/* Approve & publish — Admin only */}
                    {isAdmin && batch.tracks?.some(t => t.status === 'pendiente_aprobacion') && (
                      <div className="p-3 bg-green-500/5 border border-green-500/15 rounded-xl">
                        <p className="text-xs text-green-400 font-semibold mb-2">Tracks listos para publicar</p>
                        {batch.tracks?.filter(t => t.status === 'pendiente_aprobacion').map(t => (
                          <div key={t.id} className="flex items-center gap-2 py-1">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            <span className="text-xs text-white/70 flex-1">{t.title}</span>
                            <span className="text-[10px] text-white/30">{t.genre}</span>
                            {t.price > 0 && <span className="text-[10px] text-green-400 font-bold">${t.price}</span>}
                          </div>
                        ))}
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => publishTracks(batch.tracks.filter(t => t.status === 'pendiente_aprobacion').map(t => t.id))}
                            disabled={actionLoading === 'publish'}
                            className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-[10px] font-bold flex items-center gap-1.5 disabled:opacity-50"
                            data-testid="publish-batch-btn">
                            {actionLoading === 'publish' ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                            Aprobar Todo el Lote
                          </button>
                          <button onClick={async () => {
                            if (!window.confirm('Rechazar todos los tracks pendientes? Volveran a estado masterizado.')) return;
                            setActionLoading('reject_' + batch.batch_id);
                            try {
                              const ids = batch.tracks.filter(t => t.status === 'pendiente_aprobacion').map(t => t.id);
                              await axios.post(`${API}/api/production/reject-pending`, { track_ids: ids }, { withCredentials: true });
                              loadBatches(); loadStats();
                            } catch (e) { alert(e.response?.data?.detail || 'Error'); }
                            setActionLoading(null);
                          }}
                            disabled={actionLoading === 'reject_' + batch.batch_id}
                            className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-[10px] font-bold flex items-center gap-1.5 disabled:opacity-50"
                            data-testid="reject-batch-btn">
                            <XCircle className="w-3 h-3" /> Rechazar Lote
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Batch complete — with delete option */}
                    {batch.all_published && (
                      <div className="p-3 bg-green-500/5 border border-green-500/15 rounded-xl text-center">
                        <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-1" />
                        <p className="text-xs text-green-400 font-bold">LOTE COMPLETADO</p>
                        <p className="text-[10px] text-white/30 mb-3">{batch.total} tracks publicados en catalogo</p>
                        <button onClick={async () => {
                          if (!window.confirm('Eliminar este lote completado del historial?')) return;
                          setActionLoading('del_' + batch.batch_id);
                          try {
                            await axios.delete(`${API}/api/production/batches/${batch.batch_id}`, { withCredentials: true });
                            loadBatches();
                          } catch (e) { alert(e.response?.data?.detail || 'Error'); }
                          setActionLoading(null);
                        }}
                          disabled={actionLoading === 'del_' + batch.batch_id}
                          className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-[10px] font-bold flex items-center gap-1.5 mx-auto disabled:opacity-50"
                          data-testid="delete-batch-btn">
                          {actionLoading === 'del_' + batch.batch_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          Eliminar Lote
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ INVENTARIO ═══ */}
      {tab === 'inventory' && (
        <div className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white/70">
              <option value="">Todos los estados</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <input value={filterGenre} onChange={e => setFilterGenre(e.target.value)} placeholder="Filtrar genero..."
              className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/30 w-40 focus:outline-none focus:border-[#E8720C]/50" />
            <button onClick={loadTracks} className="p-1.5 hover:bg-white/10 rounded-lg text-white/40"><RefreshCw className="w-3.5 h-3.5" /></button>
            <span className="text-xs text-white/30 self-center ml-auto">{tracksTotal} tracks</span>
          </div>
          {tracks.length === 0 ? (
            <p className="text-center text-white/30 py-12 text-sm">No hay tracks en inventario</p>
          ) : (
            <div className="space-y-1">
              {tracks.map(t => {
                const sc = STATUS_LABELS[t.production_status] || STATUS_LABELS.generado;
                return (
                  <div key={t.id} className="flex items-center gap-3 p-3 bg-white/[0.02] hover:bg-white/[0.04] rounded-lg transition-colors">
                    <Music className="w-4 h-4 text-white/20 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white/80 font-medium truncate">{t.title}</p>
                      <p className="text-[10px] text-white/30">{t.genre} | {t.bpm} BPM {t.vibe ? `| ${t.vibe}` : ''}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold border ${sc.color}`}>{sc.label}</span>
                    {t.price && <span className="text-xs text-[#E8720C] font-bold">${t.price}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══ SETTINGS ═══ */}
      {tab === 'settings' && isAdmin && (
        <div className="space-y-4">
          <div className="glass-card p-5">
            <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Settings className="w-4 h-4 text-[#E8720C]" /> API Key de Mureka</h3>
            <p className="text-xs text-white/40 mb-3">Obtenla en platform.mureka.ai</p>
            <div className="flex gap-2">
              <input value={murekaKey} onChange={e => setMurekaKey(e.target.value)} placeholder="Bearer key..."
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/20 focus:outline-none focus:border-[#E8720C]/50" />
              <button onClick={saveMurekaKey} className="px-4 py-2 bg-[#E8720C] text-white rounded-lg text-xs font-bold">Guardar</button>
            </div>
            <p className="text-[10px] mt-2 text-white/30">{settings.mureka_configured ? 'Configurada' : 'No configurada'}</p>
          </div>
          <div className="glass-card p-5">
            <h3 className="text-sm font-semibold mb-1 flex items-center gap-2"><Settings className="w-4 h-4 text-[#E8720C]" /> Contexto Musical GPT (Memoria OG MUSIC)</h3>
            <p className="text-xs text-white/40 mb-3">Solo visible para admin y asistentes. Nunca expuesto a clientes.</p>
            <textarea value={gptContext} onChange={e => setGptContext(e.target.value)}
              placeholder="Pega aqui la memoria de ChatGPT sobre OG MUSIC..."
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/20 resize-none h-40 focus:outline-none focus:border-[#E8720C]/50" />
            <div className="flex items-center justify-between mt-2">
              <button onClick={saveGptContext} className="px-4 py-2 bg-[#E8720C] text-white rounded-lg text-xs font-bold">Guardar</button>
              <p className="text-[10px] text-white/30">{settings.gpt_context_configured ? 'Configurado' : 'No configurado'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionPanel;
